import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit3, IdCard } from "lucide-react";
import { toast } from "sonner";
import FilesPanel from "@/pages/shared/espace/FilesPanel";

const FIELDS = [
  ["matricule", "Matricule (solde)", "text"],
  ["grade", "Grade", "text"],
  ["corps", "Corps", "text"],
  ["date_prise_service", "Date de prise de service", "date"],
  ["date_entree_ecole", "Date d'arrivée dans l'école", "date"],
  ["diplome_academique", "Diplôme académique", "text"],
  ["diplome_professionnel", "Diplôme professionnel", "text"],
  ["cni", "N° CNI", "text"],
  ["telephone", "Téléphone", "tel"],
  ["situation_familiale", "Situation familiale", "text"],
  ["classe_tenue", "Classe tenue", "text"],
];
const PERSO_CATEGORIES = ["Diplôme", "Arrêté / Nomination", "Pièce d'identité", "Attestation", "Bulletin de salaire", "Autre"];

export default function InfosPerso() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);

  const load = () => api.get("/espace/profile").then(({ data }) => setProfile(data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const filled = profile && FIELDS.some(([k]) => profile[k]);
  const openForm = () => { setForm({ ...(profile || {}) }); setOpen(true); };

  const save = async () => {
    const body = Object.fromEntries([...FIELDS.map(([k]) => k), "observations"].map((k) => [k, form[k] || null]));
    try { await api.put("/espace/profile", body); toast.success("Informations enregistrées"); setOpen(false); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="ek-card p-5" data-testid="teacher-profile-card">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <IdCard className="w-5 h-5 text-[#1F7A63]" />
            <h2 className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>Informations professionnelles</h2>
          </div>
          <Button onClick={openForm} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="teacher-profile-edit-btn">
            {filled ? <Edit3 className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />} {filled ? "Modifier mes infos" : "Ajouter mes infos"}
          </Button>
        </div>
        {filled ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {FIELDS.map(([k, label]) => (
              <div key={k}>
                <dt className="text-xs text-[#5C736A]">{label}</dt>
                <dd className="text-sm font-semibold" data-testid={`teacher-info-${k}`}>{profile[k] || "—"}</dd>
              </div>
            ))}
            {profile.observations && <div className="sm:col-span-2 lg:col-span-3"><dt className="text-xs text-[#5C736A]">Observations</dt><dd className="text-sm">{profile.observations}</dd></div>}
          </dl>
        ) : (
          <p className="text-sm text-[#5C736A]">Ajoutez votre matricule, grade, diplômes… pour garder votre dossier à jour.</p>
        )}
      </div>

      <div>
        <h2 className="font-bold text-lg mb-2" style={{ fontFamily: "Outfit" }}>Mes documents personnels</h2>
        <p className="text-xs text-[#5C736A] mb-3">Visibles uniquement par vous{" "}et votre direction.</p>
        <FilesPanel scope="perso" canUpload categories={PERSO_CATEGORIES} emptyText="Aucun document. Ajoutez vos diplômes, arrêtés, attestations…" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Mes informations professionnelles</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <div key={k}><Label>{label}</Label><Input type={type} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`teacher-form-${k}`} /></div>
            ))}
            <div className="sm:col-span-2"><Label>Observations</Label><Textarea value={form.observations || ""} onChange={(e) => setForm({ ...form, observations: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="teacher-profile-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
